/* eslint-disable no-magic-numbers, sort-keys, max-lines */
/* eslint-disable sort-keys */
import { lighten } from "polished";
import textStyles from "../constants/textStyles";

export default {
    Button: {
        baseStyle: {
            ".chakra-stack > &":{
                mb: "0",
                px: "2"
            }
        },
        sizes: {
            lg: {
                h: 12,
                minW: 12,
                fontSize: "lg",
                px: 6,
            },
            md: {
                minW: "150px",
                p: 4,
                mb: "0",
                h: "12",
                fontSize: "md",
            },
            sm: {
                ...textStyles.p3,
                h: 8,
                minW: "16",
                fontSize: "sm",
                px: 3,
            },
            xs: {
                h: 6,
                minW: 6,
                fontSize: "xs",
                px: 2,
            },
        },
        variants: {
            primary: ({ colorScheme = "primary" }) => ({
                ...textStyles.p1,
                py: "0",
                borderRadius: "5",
                fontWeight: "600",
                bgColor: `${colorScheme}.400`,
                color: "white",
                textAlign: "center",
                _hover: {
                    bgColor: `${colorScheme}.500`,
                    _disabled: {
                        bgColor: `${colorScheme}.700`,
                        opacity: "0.6"
                    },
                },
                _active: {
                    bgColor: `${colorScheme}.700`,
                    _hover: {
                        bgColor: `${colorScheme}.700`,
                    }
                },
                _focus: {
                    bgColor: `${colorScheme}.700`,
                    boxShadow: "none"
                },
                _disabled: {
                    bgColor: `${colorScheme}.700`,
                    opacity: "0.6",
                }
            }),
            secondary: {
                ...textStyles.p1,
                borderRadius: "5",
                py: "4",
                bgColor: "white",
                color: "secondary.900",
                fontWeight: "600",
                border: "1px solid",
                borderColor: "secondary.300",
                textAlign: "center",
                _hover: {
                    bgColor: "secondary.100",
                    _disabled: {
                        bgColor: "secondary.200",
                        opacity: "0.5" 
                    },

                },
                _active: {
                    bgColor: "secondary.200",
                    _hover: {
                        bgColor: "secondary.100",
                    }
                },
                _focus: {
                    bgColor: "secondary.200",
                    boxShadow: "none",
                    _hover: {
                        bgColor: "secondary.200",
                    }
                },
                _disabled: {
                    bgColor: "secondary.200",
                    opacity: "0.5",
                }
            },
            linkBtn: {
                borderRadius: "0",
                ...textStyles.p2,
                bgColor: "white",
                color: "primary.400",
                fontWeight: "600",
                border: "none",
                textAlign: "right",
                justifyContent: "end",
                _hover: {
                    textDecoration: "underline",
                    _disabled: {
                        bgColor: "secondary.200",
                        opacity: "0.5" 
                    },

                },
                _active: {
                    _hover: {
                        bgColor: "secondary.100",
                    }
                },
                _focus: {
                    boxShadow: "none",
                },
                _disabled: {
                    bgColor: "secondary.200",
                    opacity: "0.5",
                }
            },
            tabButton: ({ isActive }) =>  ({
                ...textStyles.p2,
                mb: "-1px",
                bg: "none",
                py: 6,
                px: 3,
                color: isActive ? "secondary.700" : "secondary.200",
                fontWeight: isActive ? "bold" : "normal",
                borderBottom: "2px solid",
                borderBottomColor: isActive ? "primary.400" : "transparent",
                borderRadius: 0,
                minWidth: "none",
                _hover: {
                    bg: "none",
                    color: "secondary.700",
                    borderBottomColor: isActive ? "primary.400" : "transparent",
                },
                _focus: {
                    boxShadow: "none",
                    color: isActive ? "secondary.700" : "secondary.200",
                },
                _active: {
                    bg: "none",
                    color: isActive ? "secondary.700" : "secondary.200",
                }
            }),
            pagination: {
                ...textStyles.p2,
                "mb": "0",
                "bg": "none",
                "px": 3,
                "height": "auto",
                "color": "secondary.200",
                "minWidth": "none",
                "border": 0,
                "borderRadius": 0,
                "alignSelf": "stretch",
                ".chakra-button__icon svg": {
                    stroke: "secondary.200"
                },
                "&:first-of-type": {
                    borderRight: "1px solid",
                    borderRightColor: "secondary.100",
                },
                "&:last-of-type": {
                    borderLeft: "1px solid",
                    borderLeftColor: "secondary.100",
                },
                "_hover": {
                    "bg": "none",
                    "color": "secondary.700",
                    ".chakra-button__icon svg": {
                        stroke: "secondary.700"
                    },
                },
                "_focus": {
                    boxShadow: "none",
                    color: "secondary.700"
                },
                "_active": {
                    bg: "none",
                    color: "secondary.700"
                },
                "_disabled": {
                    "border": 0,
                    "opacity": 1,
                    "color": "secondary.200",
                    "borderColor": "secondary.200",
                    "&:first-of-type": {
                        borderRight: "1px solid",
                        borderRightColor: "secondary.100",
                    },
                    "&:last-of-type": {
                        borderLeft: "1px solid",
                        borderLeftColor: "secondary.100",
                    },
                    "_hover": {
                        "bg": "none",
                        "color": "secondary.200",
                        ".chakra-button__icon svg": {
                            stroke: "secondary.200"
                        },
                    },
                }
            },
        },
        defaultProps: {
            variant: "primary",
            colorScheme: "primary"
        },
    }
};

// styles created before designs were implements - kept for reference

const mode = (darkMode, lightMode) => ({ colorMode }) => colorMode === "dark" ? darkMode : lightMode;

const flatProps = {
    border: "0",
    borderRadius: "md",
    fontWeight: "normal",
    p: ({ space }) => `${space[2]} ${space[3]}`,
    _hover: {
        bgColor: "brand-dark",
    },
    _active: {
        bgColor: "brand-dark"
    },
    _focus: {
        boxShadow: "none"
    },
};

const borderBottomColor = "brand-dark";

// eslint-disable-next-line no-unused-vars
const oldstyles = {

    default: {
        bgColor: mode("black", "brand.default"),
        borderColor: mode("white", "brand-dark"),
        borderBottomColor: "brand-dark",
        px: "horizontal",
        py: "vertical",
        opacity: "1",
        _hover: { borderBottomColor },
        _active: { borderBottomColor },
        _focus: { borderBottomColor },
    },
    solid: {
        bgColor: mode("black", "brand.default"),
        borderColor: mode("white", "brand-dark"),
        borderBottomColor: "brand-dark",
        px: "horizontal",
        py: "vertical",
        opacity: "1",
        _hover: { borderBottomColor },
        _active: { borderBottomColor },
        _focus: { borderBottomColor },

    },
    primary: {
        bgColor: mode("black", "brand.default"),
        borderColor: mode("white", "brand-dark"),
        borderBottomColor: "brand-dark",
        px: "horizontal",
        py: "vertical",
        opacity: "1",
        _hover: { borderBottomColor },
        _active: { borderBottomColor },
        _focus: { borderBottomColor },

    },
    secondary: {
        border: "none",
        _active: { borderBottomColor: ({ colors }) => lighten(0.1, colors.primary["700"]) },
        _focus: { borderBottomColor: ({ colors }) => lighten(0.1,  colors.primary["700"]) },
        _hover: { borderBottomColor: ({ colors }) => lighten(0.1, colors.primary["700"]) },
        color: mode("black", "brand.default"),
        bgColor: mode("white", "white"),
        borderColor: mode("black", "white"),
        px: "horizontal",
        py: "vertical",
    },
    outline: {
        borderColor: mode("white", "brand.default"),
        bgColor: "transparent",
        color: mode("white", "brand.default"),
        _active: { background:  mode("white", "brand.default"),  },
        _focus: { background:  mode("white", "brand.default"), },
        _hover: { background:  mode("white", "brand.default"),  },
    },
    link: {
        border: "none",
        background: "none",
        color: mode("white", "brand.default"),
        paddingLeft: "0",
        paddingRight: "0",
        padding: "0",
        height: "unset",
        minWidth: 0,
        _active: { border: "none", background: "none", color: mode("white", "brand.default"), },
        _focus: { border: "none",background: "none", color: mode("white", "brand.default"), },
        _hover: { border: "none", background: "none", color: mode("white", "brand.default") },
    },
    linkDark: {
        border: "none",
        background: "none",
        color: mode("white", "dark-2"),
        paddingLeft: "0",
        paddingRight: "0",
        padding: "0",
        height: "unset",
        minWidth: 0,
        _active: { border: "none", background: "none", color: mode("white", "dark-5") },
        _focus: { border: "none",background: "none", color: mode("white", "dark-5") },
        _hover: { border: "none", background: "none", color: mode("white", "dark-5") },
    },
    linkDarkUnderline: {
        border: "none",
        background: "none",
        color: mode("white", "dark-2"),
        paddingLeft: "0",
        paddingRight: "0",
        p: 0,
        minWidth: 0,
        height: "fit-content",
        _active: { border: "none", background: "none", color: mode("white", "dark-5"), textDecoration: "underline" },
        _focus: { border: "none",background: "none", color: mode("white", "dark-5"), textDecoration: "underline" },
        _hover: { border: "none", background: "none", color: mode("white", "dark-5"), textDecoration: "underline" },
    },
    icon: {
        alignItems: "center",
        border: "none",
        h: "fit-content",
        justifyContent: "center",
        p: "0",
        w: "fit-content",
        pl: "0",
        pr: "0",
        mr: "0",
        minWidth: "0",
        _active: { border: "none", background: "none",  },
        _focus: { border: "none",background: "none", },
        _hover: { border: "none", background: "none", },
    },
    unstyled: {
        border: "0",
        pl: "0",
        pr: "0",
        height: "fit-content",
        width: "fit-content",
        bgColor: "transparent",
        _focus: { bgColor: "transparent", boxShadow: "none" },
        _hover: { bgColor: "transparent" },
        _active: { bgColor: "transparent" },
        _disabled: { bgColor: "transparent" },
    },
    progress: ({ colorScheme }) => ({
        bgColor: `${colorScheme}.400`,
        borderBottomColor: `${colorScheme}.600`,
        _hover: { borderBottomColor: `${colorScheme}.600`,
            color: mode("black", "white"),
            bgColor: ({ colors }) => lighten(0.1, colors[colorScheme]["400"]),
            borderColor: ({ colors }) => lighten(0.1, colors[colorScheme]["600"]) },
    }),
    quicklink : {
        pr: "s-2",
        // pl must be 5px to line up with other buttons in v2 - can be changed to theme value once we replace all chunk option buttons with v3
        pl: "5px", 
        py: "2",
        fontSize: "xs",
        h: "fit-content",
        w: "fit-content",
        bgColor: "transparent",
        border: "1px solid transparent",
        minWidth: "auto",
        transition: "border 0s",
        _hover: {
            bgColor: "white",
            border: ({ colors }) => `1px solid ${colors["dark-5"]}`,
            boxShadow: "workflowQuickLink",
        },
        _active: { 
            bgColor: "white",
            border: ({ colors }) => `1px solid ${colors["dark-5"]}` 
        },
        _focus: { 
            bgColor: "white",
            border: ({ colors }) => `1px solid ${colors["dark-5"]}` 
        },       
    },
    modalHeaderCloseIcon: {
        h:"7", 
        w: "7",
        bgColor:"brand-dark",
        p:"0",
        pl: "0",
        pr: "0",
        minWidth:"0",
        border:"0",
        borderRadius: 5,
        _hover: { bgColor: "brand-dark" }
    },
    flat: {
        ...flatProps
    },
    flatRound: {
        ...flatProps,
        borderRadius: "full",
        px: "8",
    },
    flatWithAnimateOnHover: {
        ...flatProps,
        _hover: {
        // eslint-disable-next-line no-underscore-dangle
            ...flatProps._hover,
            transform: "translate3d(0, -3px, 0)",
            boxShadow: "buttonHover"
        },
    },
    flatRoundWithAnimateOnHover: {
        ...flatProps,
        borderRadius: "full",
        px: "8",
        _hover: {
        // eslint-disable-next-line no-underscore-dangle
            ...flatProps._hover,
            transform: "translate3d(0, -3px, 0)",
            boxShadow: "buttonHover"
        },
    },
    tabButton: ({ isActive, colorScheme = "accent1" }) =>  ({
        bg: "none",
        py: 6,
        px: 3,
        color: isActive ? "primary" : `${colorScheme}.200`,
        fontWeight: "normal",
        borderBottom: "1px solid",
        borderBottomColor: isActive ? "brand.default" : "transparent",
        borderRadius: 0,
        minWidth: "none",
        _hover: {
            bg: "none",
            color: "primary",
            borderBottomColor: isActive ? "brand.default" : "transparent",
        },
        _focus: {
            boxShadow: "none",
            color: isActive ? "primary" : `${colorScheme}.200`,
        },
        _active: {
            bg: "none",
            color: isActive ? "primary" : `${colorScheme}.200`,
        }
    }),
    pagination: ({ colorScheme = "accent1" }) => ({
        "bg": "none",
        "py": 5,
        "px": 3,
        "height": "auto",
        "color": `${colorScheme}.200`,
        "fontWeight": "normal",
        "minWidth": "none",
        "border": 0,
        "borderRadius": 0,
        ".chakra-button__icon": {
            stroke: `${colorScheme}.200`
        },
        "&:first-of-type": {
            borderRight: "1px solid",
            borderRightColor: `${colorScheme}.50`,
        },
        "&:last-of-type": {
            borderLeft: "1px solid",
            borderLeftColor: `${colorScheme}.50`,
        },
        "_hover": {
            "bg": "none",
            "color": "text.light",
            ".chakra-button__icon": {
                stroke: "text.light"
            },
        },
        "_focus": {
            boxShadow: "none",
            color: "primary"
        },
        "_active": {
            bg: "none",
            color: "primary"
        },
        "_disabled": {
            "border": 0,
            "opacity": 1,
            "color": `${colorScheme}.200`,
            "borderColor": `${colorScheme}.50`,
            "&:first-of-type": {
                borderRight: "1px solid",
                borderRightColor: `${colorScheme}.50`,
            },
            "&:last-of-type": {
                borderLeft: "1px solid",
                borderLeftColor: `${colorScheme}.50`,
            },
        }
    }),
    flatSmall: {
        border: "0",
        borderRadius: "smallFlatButton",
        fontWeight: "normal",
        lineHeight: 1.75,
        px: ({ space }) => space[2],
        py: "s-5",
        height: "auto",
        _hover: {
            bgColor: "brand-dark"
        }
    },
    outlineListCard: {
        bgColor: "transparent",
        color: "primary",
        borderRadius: "smallFlatButton",
        fontWeight: "normal",
        lineHeight: 1.5,
        px: ({ space }) => space[1],
        py: "s-5",
        height: "auto",
        fontSize: "xs",
        border: "1px solid",
        // We have to specify each side individually so as to override the default chakra style
        borderTopColor: "light-2",
        borderLeftColor: "light-2",
        borderBottomColor: "light-2",
        borderRightColor: "light-2",
        _hover: {
            bgColor:  "light-1",
            border: "1px solid",
            // We have to specify each side individually so as to override the default chakra style
            borderTopColor: "secondary.50",
            borderLeftColor: "secondary.50",
            borderBottomColor: "secondary.50",
            borderRightColor: "secondary.50",
            color: "primary",
        },
        _focus: {
            bgColor: "transparent",
            border: "1px solid",
            // We have to specify each side individually so as to override the default chakra style
            borderTopColor: "secondary.50",
            borderLeftColor: "secondary.50",
            borderBottomColor: "secondary.50",
            borderRightColor: "secondary.50",
            color: "primary",
            boxShadow: "none"
        },
        _active: {
            bgColor:  "light-2",
            border: "1px solid",
            // We have to specify each side individually so as to override the default chakra style
            borderTopColor: "secondary.50",
            borderLeftColor: "secondary.50",
            borderBottomColor: "secondary.50",
            borderRightColor: "secondary.50",
        }
    }
};
